//useSubscriptionStore.js
import { defineStore } from "pinia";
import * as subscriptionService from "@/services/subscriptionService";

export const useSubscriptionStore = defineStore("subscription", {
  state: () => ({
    subscriptions: [],
    selectedSubscriptionIds: [],
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    userIdFilter: null, // Filtro por ID de usuário (enviado ao backend)
    shouldForcePageOne: false,

    // ✅ NOVO: última mensagem vinda do campo statusDetail da assinatura mais recente
    lastStatusDetail: null,

    // ✅ NOVO: filtros client-side aplicados sobre os dados da página atual
    filters: {
      subscriptionId: "", // inteiro parcial
      userId: "", // inteiro parcial (espelha userIdFilter no painel)
      userName: "", // busca por nome do usuário (substring; só Sys)
      planName: "", // ✅ MUDANÇA: era planId — busca por nome do plano (substring)
      paymentMethod: "", // texto livre (pix, credit_card, courtesy…)
      status: "", // SubscriptionStatus: active, cancelled, expired…
      paymentStatus: "", // PaymentStatus: approved, pending, rejected…
    },
  }),

  getters: {
    /**
     * Getter para encontrar facilmente a assinatura ativa do utilizador.
     * Isto é crucial para decidir se a ação é uma criação ou um upgrade.
     */
    activeSubscription: (state) => {
      return state.subscriptions.find(
        (sub) => sub.status && sub.status.toLowerCase() === "active",
      );
    },

    isAllSelected: (state) => (total) => {
      return state.selectedSubscriptionIds.length === total;
    },

    // ✅ NOVO: getter simples para consumir no layout (toast de expiração, unificação, etc.)
    latestStatusDetail: (state) => state.lastStatusDetail,

    // ✅ MUDANÇA: os filtros agora são aplicados no banco (backend).
    // Este getter mantém uma filtragem client-side idêntica como espelho visual
    // instantâneo: enquanto o debounce está em voo, a tabela filtra localmente
    // os dados da página atual para evitar "piscar". Quando o backend responde,
    // subscriptions já chega filtrado e os dois conjuntos convergem.
    filteredSubscriptions: (state) => {
      const f = state.filters;

      return state.subscriptions.filter((sub) => {
        // ── ID da assinatura ──────────────────────────────────────────────
        if (f.subscriptionId.trim() !== "") {
          if (
            !String(sub.subscriptionId ?? "").includes(f.subscriptionId.trim())
          )
            return false;
        }
        // ── ID do usuário ─────────────────────────────────────────────────
        if (f.userId.trim() !== "") {
          if (!String(sub.userId ?? "").includes(f.userId.trim())) return false;
        }
        // ── Nome do usuário (substring textual) ───────────────────────────
        if (f.userName.trim() !== "") {
          const uname = (sub.userName ?? "").toLowerCase();
          if (!uname.includes(f.userName.trim().toLowerCase())) return false;
        }
        // ── Nome do plano (substring textual) ─────────────────────────────
        // ✅ MUDANÇA: filtra por planName em vez de planId
        if (f.planName.trim() !== "") {
          const name = (sub.planName ?? "").toLowerCase();
          if (!name.includes(f.planName.trim().toLowerCase())) return false;
        }
        // ── Método de pagamento ───────────────────────────────────────────
        if (f.paymentMethod.trim() !== "") {
          const method = (sub.paymentMethod ?? "").toLowerCase();
          if (!method.includes(f.paymentMethod.trim().toLowerCase()))
            return false;
        }
        // ── Status da assinatura ──────────────────────────────────────────
        if (f.status !== "") {
          const subStatus = (sub.status ?? "").toLowerCase();
          if (subStatus !== f.status.toLowerCase()) return false;
        }
        // ── Status do pagamento ───────────────────────────────────────────
        if (f.paymentStatus !== "") {
          const ps = String(sub.paymentStatus ?? "").toLowerCase();
          if (ps !== f.paymentStatus.toLowerCase()) return false;
        }
        return true;
      });
    },
  },

  actions: {
    toggleSelection(id) {
      const index = this.selectedSubscriptionIds.indexOf(id);
      if (index > -1) {
        this.selectedSubscriptionIds.splice(index, 1);
      } else {
        this.selectedSubscriptionIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedSubscriptionIds = [...ids];
    },

    clearSelection() {
      this.selectedSubscriptionIds = [];
    },

    // useSubscriptionStore.js

    // ... (dentro do objeto actions)

    /**
     * ✅ MUDANÇA: Action atualizada para suportar validação vinculada ao plano.
     * @param {string} couponCode
     * @param {number} planId
     */
    async validateCoupon(couponCode, planId) {
      try {
        const response = await subscriptionService.validateCoupon(
          couponCode,
          planId,
        );
        // Retorna o CouponValidationResultDto (discountValue, finalPrice, etc)
        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message || "Erro ao validar cupom.";
        console.error("❌ Erro ao validar cupom na store:", message);
        throw new Error(message);
      }
    },

    // ... (restante do código)

    /**
     * ✅ MUDANÇA: além de popular a lista, calcula a última mensagem de status_detail
     *            com base em updatedAt/createdAt para o layout poder exibir toast.
     */
    // useSubscriptionStore.js (dentro de actions)
    // ✅ MUDANÇA: assinatura atualizada — recebe o objeto filters completo
    //            (subscriptionId, userId, planId, paymentMethod, status, paymentStatus)
    //            e os repassa ao serviço para filtragem no banco.
    //            userIdFilter separado é mantido apenas para compatibilidade com
    //            setUserIdFilter (chamado por fluxos externos como UserForm).
    async fetchSubscriptionsPaged(pageNumber, pageSize, legacyUserIdFilter) {
      try {
        // ✅ MUDANÇA: constrói o objeto de filtros mesclando legacyUserIdFilter
        //            (fluxo antigo via setUserIdFilter) com os filtros do painel.
        //            O campo userId do painel tem prioridade se preenchido;
        //            caso contrário usa legacyUserIdFilter.
        const filters = {
          subscriptionId: this.filters.subscriptionId,
          userId:
            this.filters.userId ||
            (legacyUserIdFilter != null ? String(legacyUserIdFilter) : ""),
          userName: this.filters.userName,
          planName: this.filters.planName,
          paymentMethod: this.filters.paymentMethod,
          status: this.filters.status,
          paymentStatus: this.filters.paymentStatus,
        };

        const response = await subscriptionService.fetchSubscriptionsPaged(
          pageNumber,
          pageSize,
          filters, // ✅ MUDANÇA: passa objeto em vez de userId isolado
        );

        this.subscriptions = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
        this.error = null;

        // 🔁 MUDANÇA AQUI:
        // Em vez de pegar QUALQUER statusDetail antigo,
        // agora só mostramos toast se, nesta requisição,
        // o backend marcou que houve downgrade para Free.
        //
        // Ou seja:
        // - downgradedToFreeOnThisRequest === true
        // - systemMessage preenchido
        const items = Array.isArray(this.subscriptions)
          ? this.subscriptions
          : [];

        const downgradedItems = items.filter(
          (s) =>
            s && s.downgradedToFreeOnThisRequest === true && !!s.systemMessage,
        );

        const latestDowngraded = downgradedItems.slice().sort((a, b) => {
          const ad = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bd = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bd - ad;
        })[0];

        // ✅ NOVA REGRA:
        // Só haverá lastStatusDetail (e, portanto, toast)
        // se nesta chamada houve expiração detectada e downgrade.
        this.lastStatusDetail = latestDowngraded
          ? latestDowngraded.systemMessage
          : null;
      } catch (error) {
        const status = error.response?.status;
        const message = error.response?.data?.message || error.message;

        // ✅ LOGOUT / TOKEN INVÁLIDO:
        // Durante logout voluntário, pode haver uma requisição tardia de assinaturas.
        // Se ela retornar 401, não deve virar erro global de runtime.
        if (status === 401) {
          this.error = null;
          this.lastStatusDetail = null;
          this.subscriptions = [];
          this.selectedSubscriptionIds = [];
          return;
        }

        this.error = { message };
        console.error("❌ Erro ao buscar assinaturas paginadas:", message);

        // ✅ Em caso de erro na busca, não mostramos mensagem antiga
        this.lastStatusDetail = null;

        throw new Error(message);
      }
    },

    setUserIdFilter(userId) {
      this.userIdFilter = userId;
      // ✅ NOVO: sincroniza o campo userId no painel de filtros client-side
      this.filters.userId = userId != null ? String(userId) : "";
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchSubscriptionsPaged(1, this.pageSize, userId);
    },

    // ✅ MUDANÇA: todos os filtros agora vão ao backend — qualquer alteração dispara
    //            fetchSubscriptionsPaged (voltando à página 1).
    //            userId continua chamando setUserIdFilter para manter compatibilidade
    //            com fluxos externos; os demais campos acionam o fetch diretamente.
    setFilter(field, value) {
      if (!(field in this.filters)) return;
      this.filters[field] = value;

      if (field === "userId") {
        // userId mantém o fluxo antigo: valida inteiro e chama setUserIdFilter
        // (que por sua vez chama fetchSubscriptionsPaged com os filtros atuais)
        const trimmed = String(value ?? "").trim();
        const parsed = trimmed === "" ? null : Number(trimmed);
        const isValidInt =
          trimmed === "" || (Number.isInteger(parsed) && parsed > 0);
        if (isValidInt) {
          this.setUserIdFilter(parsed);
        }
        return;
      }

      // ✅ NOVO: demais campos — dispara refetch voltando à página 1
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchSubscriptionsPaged(1, this.pageSize, this.userIdFilter);
    },

    // ✅ MUDANÇA: limpa todos os filtros e dispara refetch completo (sem filtros)
    clearFilters() {
      this.filters = {
        subscriptionId: "",
        userId: "",
        userName: "",
        planName: "",
        paymentMethod: "",
        status: "",
        paymentStatus: "",
      };
      // ✅ MUDANÇA: reseta o filtro de backend e busca todos os registros
      this.userIdFilter = null;
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchSubscriptionsPaged(1, this.pageSize, null);
    },

    async createSubscription(data) {
      try {
        await subscriptionService.createSubscription(data);
        this.shouldForcePageOne = true;
        // Recarrega a lista para refletir a nova adição
        await this.fetchSubscriptionsPaged(1, this.pageSize, this.userIdFilter);
      } catch (error) {
        console.error(
          "❌ Erro ao criar assinatura na store:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    /**
     * Action específica para o fluxo de upgrade.
     */
    async upgradeSubscription(data) {
      try {
        await subscriptionService.upgradeSubscription(data);
        // Recarrega os dados para refletir o novo estado e a assinatura antiga atualizada
        await this.fetchSubscriptionsPaged(1, this.pageSize, this.userIdFilter);
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar assinatura na store:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async updateSubscription(data) {
      try {
        await subscriptionService.updateSubscription(data);
        // Recarrega a página atual para obter dados atualizados
        await this.fetchSubscriptionsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar assinatura na store:",
          error.response?.data || error.message,
        );
        throw error;
      }
    },

    async deleteSubscriptionById(id) {
      try {
        await subscriptionService.deleteSubscription(id);
        await this.fetchSubscriptionsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        // Se a página atual ficar vazia, volta para a página anterior
        if (this.subscriptions.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchSubscriptionsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir assinatura:", message);
        throw new Error(message);
      }
    },

    async deleteSelectedSubscriptions() {
      if (!this.selectedSubscriptionIds.length) return;

      try {
        await subscriptionService.deleteSubscriptionsBatch(
          this.selectedSubscriptionIds,
        );
        this.clearSelection();
        await this.fetchSubscriptionsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );
        // Se a página atual ficar vazia, volta para a página anterior
        if (this.subscriptions.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchSubscriptionsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir assinaturas em lote:", message);
        throw new Error(message);
      }
    },

    /**
     * ✅ NOVO: Cancela uma assinatura específica usando a rota POST /api/payment/cancel/{subscriptionId}
     *          Após cancelar, recarrega as assinaturas e recalcula lastStatusDetail.
     *
     * ✅ MUDANÇA: aceita `userId` opcional.
     *
     * Contexto do bug que isso resolve:
     * Quando o cancelamento parte da aba "Resumo da Conta" (perfil do titular),
     * o reload precisa trazer as assinaturas DAQUELE usuário, página 1, com
     * pageSize amplo — não herdar `this.currentPage`/`this.userIdFilter`, que
     * podem estar com valores de uma navegação anterior no painel admin (Sys).
     * Se herdasse, a reconciliação ocorreria no banco mas a lista local viria
     * de outra página/usuário, deixando `activeSubscription` desalinhado e o
     * badge de plano stale.
     *
     * Quando `userId` é fornecido → recarrega (1, 100, userId).
     * Quando `userId` é null      → mantém o comportamento antigo do painel admin
     *                               (currentPage, pageSize, userIdFilter).
     */
    async cancelSubscription(
      subscriptionId,
      { cancelAtPeriodEnd, reason = null, userId = null },
    ) {
      try {
        // ✅ MUDANÇA: captura a resposta do POST. Antes era descartada.
        // O backend agora retorna SubscriptionCancelResponseDto também no
        // caso de reconciliação (assinatura já cancelada no MP) — com
        // Status="Cancelled" — em vez de lançar HTTP 400. O AccountSummary
        // usa esse DTO para distinguir pausa normal de pausa reconciliada.
        const response = await subscriptionService.cancelSubscription({
          subscriptionId,
          cancelAtPeriodEnd,
          reason,
        });

        if (userId != null) {
          // Fluxo de perfil: recarrega a página 1 do próprio usuário.
          await this.fetchSubscriptionsPaged(1, 100, userId);
        } else {
          // Fluxo do painel admin: mantém página/filtros atuais.
          await this.fetchSubscriptionsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }

        // ✅ MUDANÇA: devolve o DTO ao chamador (cancelActiveSubscription →
        // AccountSummary). Necessário para a abordagem A de detecção de
        // reconciliação via campo Status.
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao cancelar assinatura:", message);
        throw new Error(message);
      }
    },

    /**
     * ✅ NOVO: Cancela a assinatura ativa do usuário presente na store.
     *
     * ✅ MUDANÇA: repassa o `userId` da própria assinatura ativa para
     * cancelSubscription, garantindo que o reload pós-cancelamento traga a
     * lista correta do titular (página 1, pageSize amplo) em vez de herdar
     * página/filtros de uma navegação anterior do painel admin.
     */
    async cancelActiveSubscription({ cancelAtPeriodEnd, reason = null }) {
      const active = this.activeSubscription;
      if (!active) {
        throw new Error("Não há assinatura ativa para cancelar.");
      }
      // ✅ MUDANÇA: repassa o DTO retornado por cancelSubscription ao chamador
      // (AccountSummary), para que ele possa inspecionar o campo Status e
      // distinguir cancelamento/pausa normal de reconciliação.
      return await this.cancelSubscription(active.subscriptionId, {
        cancelAtPeriodEnd,
        reason,
        userId: active.userId ?? null,
      });
    },

    /**
     * Reativa uma assinatura pausada (preapprovalStatus = "paused") chamando
     * o backend, que por sua vez altera o preapproval no Mercado Pago de
     * "paused" para "authorized". A próxima cobrança ocorre normalmente
     * no next_payment_date já agendado.
     *
     * Espelha o padrão de cancelSubscription:
     *   - chama o serviço HTTP
     *   - recarrega a página atual para refletir o novo estado
     *
     * ✅ MUDANÇA: aceita `userId` opcional, mesmo motivo de cancelSubscription —
     * quando a reativação parte do perfil, o reload deve trazer as assinaturas
     * do titular (1, 100, userId) e não herdar página/filtros do painel admin.
     *
     * @param {number} subscriptionId
     * @param {{ reason?: string, userId?: number|null }} options
     */
    async reactivateSubscription(
      subscriptionId,
      { reason = null, userId = null } = {},
    ) {
      try {
        // ✅ MUDANÇA: captura a resposta do POST. Antes era descartada.
        // O backend agora retorna SubscriptionReactivateResponseDto também no
        // caso de reconciliação (assinatura já cancelada no MP) — com
        // Status="Cancelled" — em vez de lançar HTTP 400. O AccountSummary
        // usa esse DTO para distinguir reativação normal de reconciliação.
        const response = await subscriptionService.reactivateSubscription({
          subscriptionId,
          reason,
        });

        if (userId != null) {
          await this.fetchSubscriptionsPaged(1, 100, userId);
        } else {
          await this.fetchSubscriptionsPaged(
            this.currentPage,
            this.pageSize,
            this.userIdFilter,
          );
        }

        // ✅ MUDANÇA: devolve o DTO ao chamador (reactivateActiveSubscription →
        // AccountSummary), necessário para detectar reconciliação via Status.
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao reativar assinatura:", message);
        throw new Error(message);
      }
    },

    /**
     * Reativa a assinatura ativa (pausada) do usuário presente na store.
     * Espelha o padrão de cancelActiveSubscription para uso direto a partir
     * de telas de perfil/resumo onde se trabalha com "a assinatura atual",
     * sem precisar passar o ID explicitamente.
     *
     * ✅ MUDANÇA: repassa o `userId` da assinatura ativa para garantir reload
     * correto do titular.
     */
    async reactivateActiveSubscription({ reason = null } = {}) {
      const active = this.activeSubscription;
      if (!active) {
        throw new Error("Não há assinatura ativa para reativar.");
      }
      // ✅ MUDANÇA: repassa o DTO retornado por reactivateSubscription ao
      // chamador (AccountSummary), para que ele inspecione o campo Status e
      // distinga reativação normal de reconciliação.
      return await this.reactivateSubscription(active.subscriptionId, {
        reason,
        userId: active.userId ?? null,
      });
    },

    // ✅ NOVO: concede acesso cortesia via POST /api/Subscriptions/courtesy
    async grantCourtesy(payload) {
      try {
        const response = await subscriptionService.grantCourtesy(payload);
        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Erro ao conceder cortesia.";
        console.error("❌ Erro ao conceder cortesia:", message);
        throw error; // relança o objeto original para o modal ler response.data.message
      }
    },
    // ✅ NOVO: action para expirar todas as assinaturas vencidas de uma vez.
    // Após a operação, recarrega a lista para refletir os novos status.
    async expireOverdueAsync() {
      try {
        const response = await subscriptionService.expireOverdue();

        // Recarrega a página atual para refletir os status atualizados
        await this.fetchSubscriptionsPaged(
          this.currentPage,
          this.pageSize,
          this.userIdFilter,
        );

        return response.data; // ExpireOverdueResultDto
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Erro ao expirar assinaturas.";
        console.error("❌ Erro ao expirar assinaturas:", message);
        throw new Error(message);
      }
    },
    /**
     * Verifica se a assinatura ativa é elegível para estorno.
     * Chama GET /api/subscriptions/{id}/refund-eligibility.
     *
     * @param {number} subscriptionId
     * @returns {{
     *   eligible: boolean,
     *   message: string,
     *   windowEndsAt: string|null,
     *   paymentMethod: string|null,
     *   refundAmount: number|null,
     *   creditBalanceRefundAmount: number|null
     * }} RefundEligibilityDto — repassado cru do backend (response.data).
     */
    async checkRefundEligibility(subscriptionId) {
      try {
        const response = await subscriptionService.checkRefundEligibility(
          subscriptionId,
        );
        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Erro ao verificar elegibilidade de estorno.";
        console.error("❌ Erro ao verificar elegibilidade:", message);
        throw new Error(message);
      }
    },

    /**
     * Solicita estorno e downgrade para Free da assinatura ativa.
     * Chama POST /api/subscriptions/{id}/refund-and-downgrade.
     * Após sucesso, recarrega as assinaturas para refletir o novo estado.
     *
     * @param {number} subscriptionId
     * @returns {{ subscriptionId, refundSucceeded, mpRefundStatus, message }}
     */
    async refundAndDowngrade(subscriptionId) {
      try {
        const response = await subscriptionService.refundAndDowngrade(
          subscriptionId,
        );

        // Recarrega para refletir o novo status (Free)
        await this.fetchSubscriptionsPaged(1, this.pageSize, this.userIdFilter);

        return response.data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Erro ao processar estorno.";
        console.error("❌ Erro ao solicitar estorno:", message);
        throw new Error(message);
      }
    },
  },
});
